import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  EmojiEventsOutlined,
  LeaderboardOutlined,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga';
import { serverEnvironment, defaultCharacters } from 'configs/app-config.js';
import { collectionLinks } from 'configs/collectionData.js';
import { collectionLabels } from 'configs/collectionLabels';
import { collectionLeaderboardRows } from 'features/v2/presentation';
import V2PageShell from 'features/v2/V2PageShell';
import { v2Tokens } from 'theme/v2Theme';
import 'styles/v2.css';

const metricOptions = [
  { id: 'frequency', label: 'Most played' },
  { id: 'highestScore', label: 'Highest score' },
];

const rankColor = (index) => {
  if (index === 0) return '#F7931A';
  if (index === 1) return '#E5E5E5';
  if (index === 2) return 'rgba(247,147,26,0.72)';
  return '#737373';
};

const PublicLeaderboardPage = () => {
  ReactGA.initialize('G-RF6770D6JZ');
  const navigate = useNavigate();
  const [collections, setCollections] = useState({});
  const [status, setStatus] = useState('loading');
  const [metric, setMetric] = useState('frequency');
  const labels = useMemo(() => collectionLabels(), []);

  const loadLeaderboard = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await fetch(`${serverEnvironment}/play/scores/event`);
      if (!response.ok) throw new Error('Leaderboard request failed');
      const data = await response.json();
      if (!data || !data.collections || typeof data.collections !== 'object') {
        throw new Error('Leaderboard response was invalid');
      }
      setCollections(data.collections);
      setStatus('success');
    } catch {
      setCollections({});
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
    loadLeaderboard();
  }, [loadLeaderboard]);

  const rows = useMemo(
    () => collectionLeaderboardRows(collections, metric),
    [collections, metric]
  );

  const collectionImage = (row) => {
    if (collectionLinks[row.name] && collectionLinks[row.name].image) {
      return collectionLinks[row.name].image;
    }
    if (defaultCharacters[row.name] && defaultCharacters[row.name][0]) {
      return `/assets/collections/${row.name}/${defaultCharacters[row.name][0]}.png`;
    }
    return '';
  };

  const collectionName = (row) => labels[row.name] || row.name;

  return (
    <V2PageShell>
      <Container
        component="main"
        maxWidth={false}
        sx={{ width: '100%', maxWidth: 960, px: { xs: 2, sm: 3 }, py: { xs: 3, md: 6 } }}
      >
          <Button
            color="inherit"
            variant="text"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ mb: 2 }}
          >
            Back to games
          </Button>

          <Card
            component="section"
            aria-labelledby="leaderboard-title"
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: v2Tokens.radius.panel,
              border: '1px solid rgba(255,255,255,0.08)',
              backgroundColor: v2Tokens.surface,
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.32)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="overline" color="primary.main">
                  Production Records
                </Typography>
                <Typography id="leaderboard-title" component="h1" variant="h2" sx={{ mt: 0.75 }}>
                  Historical collection leaderboard
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 580, fontSize: 12, lineHeight: 1.65 }}>
                  Collection activity and highest scores returned by the existing production event summary. No local or simulated records are included.
                </Typography>
              </Box>
              <EmojiEventsOutlined sx={{ fontSize: 42, color: 'primary.main' }} />
            </Stack>

            <Box
              role="group"
              aria-label="Leaderboard sort"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 0.5,
                p: 0.5,
                mb: 3,
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: v2Tokens.radius.nested,
                backgroundColor: 'rgba(0,0,0,0.28)',
              }}
            >
              {metricOptions.map((option) => {
                const selected = option.id === metric;
                return (
                  <Button
                    key={option.id}
                    aria-pressed={selected}
                    color={selected ? 'primary' : 'inherit'}
                    onClick={() => setMetric(option.id)}
                    sx={{
                      minHeight: 42,
                      border: '1px solid',
                      borderColor: selected
                        ? 'rgba(247,147,26,0.30)'
                        : 'transparent',
                      backgroundColor: selected
                        ? 'rgba(247,147,26,0.10)'
                        : 'transparent',
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>

            {status === 'loading' && (
              <Stack spacing={1} aria-label="Loading leaderboard">
                {[0, 1, 2, 3, 4, 5].map((item) => (
                  <Skeleton key={item} variant="rounded" height={64} />
                ))}
              </Stack>
            )}

            {status === 'error' && (
              <Box
                role="alert"
                sx={{
                  p: 2.5,
                  border: '1px solid rgba(239,68,68,0.32)',
                  borderRadius: v2Tokens.radius.nested,
                  backgroundColor: 'rgba(127,29,29,0.20)',
                }}
              >
                <Typography color="error.light" sx={{ fontWeight: 800 }}>
                  The leaderboard could not be loaded.
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 12 }}>
                  No placeholder records are shown when production data is unavailable.
                </Typography>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadLeaderboard}
                  sx={{ mt: 2 }}
                >
                  Retry
                </Button>
              </Box>
            )}

            {status === 'success' && rows.length === 0 && (
              <Stack alignItems="center" spacing={1} sx={{ py: 10, textAlign: 'center' }}>
                <LeaderboardOutlined color="disabled" sx={{ fontSize: 42 }} />
                <Typography sx={{ fontWeight: 800 }}>No leaderboard records</Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 360, fontSize: 12 }}>
                  The production event summary returned no collection entries.
                </Typography>
              </Stack>
            )}

            {status === 'success' && rows.length > 0 && (
              <>
                <TableContainer
                  className="v2-desktop-only"
                  sx={{
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: v2Tokens.radius.nested,
                    backgroundColor: 'rgba(0,0,0,0.18)',
                  }}
                >
                  <Table aria-label="Collection leaderboard">
                    <TableHead>
                      <TableRow>
                        {['Rank', 'Collection', 'Plays', 'Highest score'].map((heading) => (
                          <TableCell
                            key={heading}
                            sx={{
                              borderColor: 'rgba(255,255,255,0.07)',
                              color: 'text.secondary',
                              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                            }}
                          >
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <TableRow
                          key={row.name}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.035)' },
                          }}
                        >
                          <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                            <Box
                              sx={{
                                display: 'grid',
                                placeItems: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                color: rankColor(index),
                                border: index < 3 ? `1px solid ${rankColor(index)}` : '1px solid transparent',
                                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                                fontSize: 11,
                                fontWeight: 800,
                              }}
                            >
                              {index + 1}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                src={collectionImage(row) || undefined}
                                alt={collectionName(row)}
                                variant="rounded"
                                sx={{ width: 42, height: 42, bgcolor: v2Tokens.surfaceInset }}
                              >
                                <LeaderboardOutlined fontSize="small" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                                  {collectionName(row)}
                                </Typography>
                                <Typography variant="overline" color="text.secondary">
                                  {row.name}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)', fontWeight: 800 }}>
                            {row.frequency}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)', color: 'primary.main', fontWeight: 900 }}>
                            {row.highestScore}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Stack className="v2-mobile-only" spacing={1}>
                  {rows.map((row, index) => (
                    <Box
                      key={row.name}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '32px 44px minmax(0, 1fr)',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: v2Tokens.radius.nested,
                        backgroundColor: 'rgba(0,0,0,0.18)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: rankColor(index),
                          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                          fontWeight: 900,
                        }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </Typography>
                      <Avatar
                        src={collectionImage(row) || undefined}
                        alt={collectionName(row)}
                        variant="rounded"
                        sx={{ width: 44, height: 44, bgcolor: v2Tokens.surfaceInset }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 800 }}>
                          {collectionName(row)}
                        </Typography>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                          <Chip label={`${row.frequency} plays`} size="small" />
                          <Chip label={`${row.highestScore} score`} color="primary" variant="outlined" size="small" />
                        </Stack>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Card>
      </Container>
    </V2PageShell>
  );
};

export default PublicLeaderboardPage;
